import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";

import { BLOCKING_CONCERN_CODES, CONCERN_CATALOG } from "./concerns";

type Filter = "all" | "blocking" | "advisory" | "applied";

const severityLabel = (
  code: number,
  severity: number
): { label: string; cls: string } => {
  if (BLOCKING_CONCERN_CODES.has(code))
    return { label: "Blocks write", cls: "bg-red-100 text-red-700" };
  if (severity === 2)
    return { label: "Warning", cls: "bg-amber-100 text-amber-800" };
  return { label: "Info", cls: "bg-gray-100 text-gray-600" };
};

/**
 * Right-side reference drawer listing every concern code the sanity checks can
 * apply — searchable/filterable — so a reviewer can look up what any flag means
 * (and which ones block a write). Codes applied to the current item are marked
 * and can be isolated with the "On this item" filter.
 */
export const ConcernCatalogDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
  /** Codes applied to the food currently in view (highlighted + filterable). */
  activeCodes?: number[];
}> = ({ open, onClose, activeCodes }) => {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Filter>("all");

  // Reset the search/filter each time the drawer is opened fresh.
  useEffect(() => {
    if (open) {
      setQ("");
      setFilter(activeCodes && activeCodes.length > 0 ? "applied" : "all");
    }
  }, [open, activeCodes]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const active = useMemo(() => new Set(activeCodes || []), [activeCodes]);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return Object.values(CONCERN_CATALOG)
      .filter((c) => {
        if (filter === "blocking" && !BLOCKING_CONCERN_CODES.has(c.code))
          return false;
        if (filter === "advisory" && BLOCKING_CONCERN_CODES.has(c.code))
          return false;
        if (filter === "applied" && !active.has(c.code)) return false;
        if (!term) return true;
        return (
          String(c.code).includes(term) ||
          c.name.toLowerCase().includes(term) ||
          c.description.toLowerCase().includes(term)
        );
      })
      .sort((a, b) => a.code - b.code);
  }, [q, filter, active]);

  if (!open) return null;

  const filterBtn = (key: Filter, label: string) => (
    <button
      type="button"
      onClick={() => setFilter(key)}
      className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
        filter === key
          ? "bg-indigo-600 text-white"
          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
      }`}
    >
      {label}
    </button>
  );

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[1050] flex justify-end"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-gray-900/50" onClick={onClose} />
      <div className="relative z-10 flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Concern codes
            </h3>
            <p className="text-xs text-gray-500">
              Sanity-check flags applied during ingest. Red ones block a write.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Controls */}
        <div className="space-y-2 border-b border-gray-100 px-5 py-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by code, name, or description…"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
          />
          <div className="flex flex-wrap gap-1.5">
            {filterBtn("all", "All")}
            {filterBtn("blocking", "Blocking")}
            {filterBtn("advisory", "Advisory")}
            {activeCodes && activeCodes.length > 0
              ? filterBtn("applied", `On this item (${activeCodes.length})`)
              : null}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-400">
              No concern codes match.
            </p>
          ) : (
            <ul className="space-y-2">
              {rows.map((c) => {
                const sev = severityLabel(c.code, c.severity);
                const isActive = active.has(c.code);
                return (
                  <li
                    key={c.code}
                    className={`rounded-lg border p-3 ${
                      isActive
                        ? "border-indigo-300 bg-indigo-50/60"
                        : "border-gray-200 bg-white"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-gray-400">
                          {c.code}
                        </span>
                        <span className="text-sm font-semibold text-gray-900">
                          {c.name}
                        </span>
                      </div>
                      <span
                        className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${sev.cls}`}
                      >
                        {sev.label}
                      </span>
                    </div>
                    <p className="mt-1 text-xs leading-snug text-gray-600">
                      {c.description}
                    </p>
                    {isActive && (
                      <p className="mt-1 text-[11px] font-semibold text-indigo-600">
                        ● Applied to this item
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConcernCatalogDrawer;
